import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Play, X, Clock } from "lucide-react";

const VIDEOS = [
  {
    file_name: "1791887717298_How_to_view_attendance_and_check_worker_profile.mp4",
    video_url:
      "https://fk-video-interviews.s3.ap-south-1.amazonaws.com/supervisor+demo+video/1791887717298_How_to_view_attendance_and_check_worker_profile.mp4",
  },
  {
    file_name: "1791887718473_How_to_mark_attendance_.mp4",
    video_url:
      "https://fk-video-interviews.s3.ap-south-1.amazonaws.com/supervisor+demo+video/1791887718473_How_to_mark_attendance_.mp4",
  },
  {
    file_name: "1791887719263_How_to_allocate_department.mp4",
    video_url:
      "https://fk-video-interviews.s3.ap-south-1.amazonaws.com/supervisor+demo+video/1791887719263_How_to_allocate_department.mp4",
  },
];

// "1791887718473_How_to_mark_attendance_.mp4" -> "How to mark attendance"
const getTitle = (fileName) =>
  fileName
    .replace(/\.[^/.]+$/, "")
    .replace(/^\d+_/, "")
    .replace(/_/g, " ")
    .trim();

// 125 -> "2:05"
const formatDuration = (secs) => {
  if (!secs || !isFinite(secs)) return "";
  const total = Math.round(secs);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

const VideoRow = ({ video, onSelect }) => {
  const [duration, setDuration] = useState("");

  return (
    <button
      onClick={() => onSelect(video)}
      className="grid grid-cols-2 gap-3 items-stretch text-left rounded-xl p-2 border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors group w-full"
    >
      {/* Left: thumbnail */}
      <div className="relative w-full aspect-video bg-gray-900 rounded-lg overflow-hidden">
        <video
          src={`${video.video_url}#t=0.5`}
          preload="metadata"
          muted
          playsInline
          onLoadedMetadata={(e) => setDuration(formatDuration(e.currentTarget.duration))}
          className="w-full h-full object-cover pointer-events-none"
        />
      </div>

      {/* Right: title + details */}
      <div className="min-w-0 flex flex-col justify-between py-0.5">
        <span className="text-sm sm:text-base font-semibold text-gray-900 leading-snug break-words">
          {getTitle(video.file_name)}
        </span>

        <div className="flex flex-col gap-1.5 mt-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] sm:text-xs font-medium">
              Demo video
            </span>
            {duration && (
              <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs text-gray-500">
                <Clock className="h-3 w-3" />
                {duration}
              </span>
            )}
          </div>
          <span className="inline-flex items-center gap-1 self-start px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[11px] sm:text-xs font-semibold group-hover:bg-indigo-700 transition-colors">
            <Play className="h-3 w-3" fill="currentColor" />
            Watch now
          </span>
        </div>
      </div>
    </button>
  );
};

const MyVideos = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  // Close player on Escape
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  return (
    <>
      <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
        <div
          className="w-full bg-white shadow-lg rounded-lg px-4 sm:px-6 pb-8 sm:pb-10"
          style={{ paddingTop: "calc(var(--header-height) + 16px)" }}
        >
          <div className="flex items-center gap-3 mb-4 max-w-2xl mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5 text-gray-700" />
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900">Tutorials</h1>
          </div>

          <div className="flex flex-col gap-3 max-w-2xl mx-auto">
            {VIDEOS.map((video) => (
              <VideoRow key={video.file_name} video={video} onSelect={setSelected} />
            ))}
          </div>
        </div>
      </div>

      {/* Video player modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-3xl bg-black rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 bg-white">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900 pr-3">
                {getTitle(selected.file_name)}
              </h2>
              <button
                onClick={() => setSelected(null)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close video"
              >
                <X className="h-5 w-5 text-gray-700" />
              </button>
            </div>
            <video
              key={selected.video_url}
              src={selected.video_url}
              controls
              autoPlay
              playsInline
              className="w-full max-h-[75vh] bg-black"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default MyVideos;